import {
    ICart,
    ICartAddItem,
    ICartChangeItem,
    ICartFetchOptions,
    ICartItem,
    ICartItemProperties,
    ICartItems,
    ICartManager,
    ICartPostOptions,
    ICartUpdateItem
} from "../../Interfaces/Cart";
import { CartItem } from "./CartItem";
import { HTTPError } from "../../Exceptions/HTTPError";
import DropdownButton from "../../Components/Form/DropdownButton";
import {
    META_PRODUCT_TYPES,
    PRODUCT_SERVICES_KEY,
    SELECTORS,
    SERVICE_BELONGS_TO_KEY,
    SERVICE_PROPERTY_MAP
} from "./Constants";
import { Dropdown } from "../../Components/Form/Dropdown";
import { ProductVariantSelect } from "../../Components/ProductVariantSelect";
import giftManager, { GiftManager } from "./GiftManager";

/**
 * @see https://shopify.dev/api/ajax/reference/cart
 * provide methods to add/change/update items to the cart / load/clear the cart
 */
class CartManager implements ICartManager {
    private static SELECTORS = SELECTORS;

    private static URLS: { [key: string]: string } = {
        BASE: window.Shopify.routes.root,
        CART: "cart.js",
        ADD: "add.js",
        UPDATE: "update.js",
        CHANGE: "change.js",
        CLEAR: "clear.js"
    };

    public static ACTION_TYPES = {
        ADD: "add",
        CHANGE: "change",
        LOAD: "load",
        UPDATE: "update",
        CLEAR: "clear"
    };

    public static CTA_DROPDOWN_TYPES = {
        LOCATION: "location"
    };

    private static SERVICE_BELONGS_TO_KEY = SERVICE_BELONGS_TO_KEY;
    private static PRODUCT_SERVICES_KEY = PRODUCT_SERVICES_KEY;

    private static METHOD_GET = "GET";
    private static METHOD_POST = "POST";

    private static STATUS_CODES: { [key: string]: number } = {
        SUCCESS: 200
    };

    constructor() {
        this.initAddToCartButtons();
        this.initCartItems();
    }

    private initCartItems() {
        // Init cart-service-items
        const cartItems = document.querySelectorAll(CartManager.SELECTORS.CART_ITEM) as NodeListOf<HTMLElement>;
        this.load().then((cart) => {
            cartItems.forEach((cartItem) => new CartItem(cartItem, cart.items));
        });
    }

    private initAddToCartButtons() {
        const addButtons = document.querySelectorAll(CartManager.SELECTORS.ADD) as NodeListOf<
            HTMLButtonElement | DropdownButton
        >;
        addButtons.forEach((addButton) => {
            addButton.addEventListener("click", () => {
                const variantId = parseInt(addButton.dataset.variant);
                const quantity = parseInt(addButton.dataset.quantity) || 1;
                const properties = JSON.parse(addButton.dataset.properties || "{}");
                if (CartManager.isLocationChooser(addButton)) {
                    properties._location = {};
                    (<DropdownButton>addButton).options.forEach((option) => {
                        properties._location[option.value] = option.value === addButton.value;
                    });
                }
                void this.add(variantId, quantity, properties);
            });
        });
        this.initVariantSelect();
    }

    /**
     * Instantiate handler for variant selects on PDP
     * @private
     */
    private initVariantSelect(): void {
        document
            .querySelectorAll<Dropdown>(CartManager.SELECTORS.VARIANT_SELECT)
            .forEach((variantSelect) => new ProductVariantSelect(variantSelect));
    }

    /**
     * determine if button provides a location
     * @param {HTMLButtonElement|DropdownButton} btn
     * @private
     */
    private static isLocationChooser(btn: HTMLButtonElement | DropdownButton): boolean {
        return btn instanceof DropdownButton && btn.classList.contains(CartManager.CTA_DROPDOWN_TYPES.LOCATION);
    }

    /**
     * load cart
     * @returns {Promise}
     */
    async load(): Promise<ICart> {
        try {
            CartManager.dispatchStart(CartManager.ACTION_TYPES.LOAD);
            const response = await CartManager.get(`${CartManager.URLS.BASE}${CartManager.URLS.CART}`);
            const cart = await response.json();
            CartManager.dispatchSuccess(CartManager.ACTION_TYPES.LOAD, cart);
            return cart;
        } catch (error) {
            CartManager.dispatchError(CartManager.ACTION_TYPES.LOAD, error);
        }
    }

    /**
     * add item to cart
     * @param {String} id
     * @param {Number} quantity
     * @param properties
     * @returns {Promise}
     */
    async add(id: number, quantity: number = 1, properties: ICartItemProperties = {}): Promise<ICartItems> {
        return this.addMultiple([{ id, quantity, properties }]);
    }

    /**
     * Add items to cart
     * @returns {Promise}
     */
    async addMultiple(items: ICartAddItem[]): Promise<ICartItems> {
        try {
            CartManager.dispatchStart(CartManager.ACTION_TYPES.ADD);
            const response = await CartManager.post(CartManager.getCartUrl("ADD"), { items });
            const cartItems = await response.json();
            CartManager.dispatchSuccess(CartManager.ACTION_TYPES.ADD, cartItems);
            // reload the cart since the cartItems consists of added item only
            const cart = await this.load();
            if (giftManager instanceof GiftManager) {
                await giftManager.handleGifts(cart);
            }
            return cartItems;
        } catch (error) {
            CartManager.dispatchError(CartManager.ACTION_TYPES.ADD, error);
        }
    }

    async simpleAdd(id: number, quantity: number = 1, properties: ICartItemProperties = {}) {
        try {
            await CartManager.post(CartManager.getCartUrl("ADD"), { items: [{ id, quantity, properties }] });
        } catch (error) {
            CartManager.dispatchError(CartManager.ACTION_TYPES.ADD, error);
        }
    }

    /**
     * clear cart
     * @returns {Promise}
     */
    async clear(): Promise<ICart> {
        try {
            CartManager.dispatchStart(CartManager.ACTION_TYPES.CLEAR);
            const response = await CartManager.post(CartManager.getCartUrl("CLEAR"));
            const cart = await response.json();
            CartManager.dispatchSuccess(CartManager.ACTION_TYPES.CLEAR, cart);
            return cart;
        } catch (error) {
            CartManager.dispatchError(CartManager.ACTION_TYPES.CLEAR, error);
        }
    }

    /**
     * update item quantities in cart
     * @param updates line item key with the new quantity
     * @returns {Promise}
     */
    async updateQuantities(updates: { [key: string]: number }): Promise<ICart> {
        try {
            CartManager.dispatchStart(CartManager.ACTION_TYPES.UPDATE);
            const response = await CartManager.post(CartManager.getCartUrl("UPDATE"), { updates });
            let cart = await response.json();
            if (giftManager instanceof GiftManager) {
                cart = await giftManager.handleGifts(cart);
            }
            CartManager.dispatchSuccess(CartManager.ACTION_TYPES.UPDATE, cart);
            return cart;
        } catch (error) {
            CartManager.dispatchError(CartManager.ACTION_TYPES.UPDATE, error);
        }
    }

    /**
     * update item quantity in cart
     * @param lineKey
     * @param quantity
     * @returns {Promise}
     */
    async updateQuantity(lineKey: string, quantity: number): Promise<ICart> {
        return this.updateQuantities({
            [lineKey]: quantity
        });
    }

    /**
     * change cart item property
     * @param {String} lineKey
     * @param {Object.<String: String>} properties
     * @param {Object.<String: String>[]} serviceProperties
     * @param serviceQuantities
     * @returns {Promise}
     */
    async changeProperties(
        lineKey: string,
        properties: ICartItemProperties,
        serviceQuantities?: {
            id: string;
            quantity: number;
        }[],
        serviceProperties?: { [key: string]: ICartItemProperties }
    ): Promise<ICart> {
        try {
            CartManager.dispatchStart(CartManager.ACTION_TYPES.CHANGE);
            const oldCart = await this.load();
            const item = oldCart.items.find((i: ICartItem) => i.key === lineKey);
            const response = await CartManager.post(CartManager.getCartUrl("CHANGE"), {
                id: lineKey,
                quantity: item?.quantity,
                properties: { ...item?.properties, ...properties }
            });
            await this.updateCartServiceItems(oldCart, item.id, properties, serviceQuantities, serviceProperties);
            let cart = await response.json();
            if (giftManager instanceof GiftManager) {
                cart = await giftManager.handleGifts(cart);
            }
            CartManager.dispatchSuccess(CartManager.ACTION_TYPES.CHANGE, cart);
            return cart;
        } catch (error) {
            CartManager.dispatchError(CartManager.ACTION_TYPES.CHANGE, error);
        }
    }

    async updateServiceProperties(
        lineKey: string,
        properties: string[],
        serviceQuantities?: {
            id: string;
            quantity: number;
        }[],
        serviceProperties?: { [key: string]: ICartItemProperties }
    ): Promise<ICart> {
        return this.changeProperties(
            lineKey,
            {
                [CartManager.PRODUCT_SERVICES_KEY]: properties
            },
            serviceQuantities,
            serviceProperties
        );
    }

    /**
     * Update (meta)products in cart to represent services after the properties changed
     * @param cart
     * @param id
     * @param properties
     * @param serviceQuantities
     * @param serviceProperties
     * @private
     */
    private async updateCartServiceItems(
        cart: ICart,
        id: number,
        properties: ICartItemProperties,
        serviceQuantities?: {
            id: string;
            quantity: number;
        }[],
        serviceProperties?: { [key: string]: ICartItemProperties }
    ) {
        // collect items
        const item = cart.items.find((i) => i.id === id);
        const currentServices = cart.items.filter(
            (i) => META_PRODUCT_TYPES.includes(i.product_type) && i.properties[CartManager.SERVICE_BELONGS_TO_KEY] == id
        );

        // Check existing service products for required quantity updates
        const serviceIds = properties[CartManager.PRODUCT_SERVICES_KEY] as string[];
        const updates: { [key: string]: number } = {};
        for (const service of currentServices) {
            const optionalQuantityKey = SERVICE_PROPERTY_MAP[service.handle]?.service_quantity;
            const selectedQuantity = serviceQuantities.find((q) => q.id === String(service.id));
            // remove if no quantity is given
            if (!selectedQuantity) {
                updates[service.key] = 0;
            } else if (!serviceIds.includes(String(service.id))) {
                const decrement = (item.properties?.[optionalQuantityKey] as number) || 1;
                updates[service.key] = Math.max(service.quantity - decrement, 0);
            } else if (selectedQuantity) {
                updates[service.key] = Math.max(selectedQuantity.quantity, 0);
            }
        }
        if (Object.keys(updates).length) await this.updateQuantities(updates);

        // Variant ID and quantity
        const serviceProductsToAdd: ICartAddItem[] = [];
        // Add required new service products
        for (const service of serviceIds) {
            const isServiceAdded = currentServices.find((s) => s.id === parseInt(service));
            // Prevent unwanted re-adding of service products
            if (isServiceAdded) continue;
            serviceProductsToAdd.push({
                id: parseInt(service),
                quantity: serviceQuantities.find((q) => q.id === service).quantity,
                properties: {
                    ...{ [CartManager.SERVICE_BELONGS_TO_KEY]: id },
                    ...serviceProperties[service]
                }
            });
        }
        if (serviceProductsToAdd.length) await this.addMultiple(serviceProductsToAdd);
    }

    /**
     * obtain cart url
     * @param {String} part
     * @private
     */
    private static getCartUrl(part: string): string {
        return `${CartManager.URLS.BASE}cart/${CartManager.URLS[part]}`;
    }

    /**
     * post request to cart api
     * @param {String} url
     * @param {Object} body
     * @returns {Promise}
     * @private
     */
    private static async post(
        url: string,
        body?: ICartPostOptions | ICartChangeItem | ICartUpdateItem
    ): Promise<Response> {
        let options: ICartFetchOptions = {
            method: CartManager.METHOD_POST,
            headers: {
                "Content-Type": "application/json"
            }
        };
        if (body) {
            options = { ...options, body: JSON.stringify(body) };
        }
        return await this.fetch(CartManager.METHOD_POST, url, options);
    }

    /**
     * get request to cart api
     * @param {String} url
     * @returns {Promise}
     * @private
     */
    private static async get(url: string): Promise<Response> {
        const options: ICartFetchOptions = {
            method: CartManager.METHOD_GET,
            headers: {
                "Content-Type": "application/json"
            }
        };
        return await CartManager.fetch(CartManager.METHOD_GET, url, options);
    }

    /**
     * do fetch
     * @param {String} method
     * @param {String} url
     * @param {Object} options
     * @returns {Promise}
     * @private
     */
    private static async fetch(method: string, url: string, options: ICartFetchOptions): Promise<Response> {
        const response = await fetch(url, options);

        if (response.status !== CartManager.STATUS_CODES.SUCCESS) {
            const error = new HTTPError(response.status, await response.text());
            CartManager.dispatchError("http", error);
            throw error;
        }

        window.dispatchEvent(new CustomEvent("cart-http-success", { detail: response }));
        return response;
    }

    /**
     * dispatch start event
     * @param {String} type
     * @private
     */
    private static dispatchStart(type: string): void {
        window.dispatchEvent(new CustomEvent(`cart-${type}-start`));
    }

    /**
     * dispatch success event
     * @param {String} type
     * @param {Object} detail
     * @private
     */
    private static dispatchSuccess(type: string, detail: ICart | ICartItems): void {
        window.dispatchEvent(new CustomEvent(`cart-${type}-success`, { detail }));
        if (CartManager.ACTION_TYPES.ADD !== type) {
            window.dispatchEvent(new CustomEvent(`cart-change`, { detail }));
        }
    }

    /**
     * dispatch error to console and event listeners
     * @param {String} type
     * @param {Error} detail
     * @private
     */
    private static dispatchError(type: string, detail: Error): void {
        console.error(detail);
        window.dispatchEvent(new CustomEvent(`cart-${type}-error`, { detail }));
    }
}

const cartManager = new CartManager();
window.cartManager = cartManager; // for testing purposes
export default cartManager;
export { CartManager };
